import React, { forwardRef } from 'react';

/** Shared shadow for association popovers (keeps visual parity with design). */
const ASSOCIATION_POPOVER_BOX_SHADOW =
  '0 12px 28px -10px rgb(0 0 0 / 0.24), 0 6px 12px -8px rgb(0 0 0 / 0.18)';

/** Horizontal padding for header + body (16px). */
const POPOVER_PAD_X = 'px-4';

function RcmControlsSubtitle() {
  return (
    <p className="mt-2 text-xs leading-relaxed text-gray-600">
      Each block is{' '}
      <strong className="font-semibold text-gray-800">this risk in one process</strong>
      ; listed controls are the mitigations for that pairing (what you would export as RCM rows).
    </p>
  );
}

const AssociationPopover = forwardRef(function AssociationPopover(
  {
    isOpen,
    dataAttr,
    top,
    left,
    title,
    items = [],
    primaryTextKey,
    secondaryTextKey,
    actionLabel,
    onItemAction,
    controlGroups,
  },
  ref
) {
  if (!isOpen) return null;

  const isRcmControls = Array.isArray(controlGroups) && controlGroups.length > 0;

  const listScrollClass = 'max-h-[22rem] overflow-y-auto [scrollbar-gutter:stable]';

  const containerStyle = {
    top,
    left,
    boxShadow: ASSOCIATION_POPOVER_BOX_SHADOW,
  };

  return (
    <div
      ref={ref}
      {...(dataAttr ? { [dataAttr]: true } : {})}
      className={`fixed z-30 max-w-[calc(100vw-2rem)] overflow-hidden bg-white border border-gray-300 rounded-md ${
        isRcmControls ? 'w-[min(28rem,calc(100vw-2rem))]' : 'w-80'
      }`}
      style={containerStyle}
    >
      {/* Title + intro — same horizontal padding as body blocks */}
      <div className={`${POPOVER_PAD_X} border-b border-gray-200 bg-white pt-4 pb-3`}>
        <p className="text-base font-semibold tracking-tight text-gray-900">{title}</p>
        {isRcmControls ? <RcmControlsSubtitle /> : null}
      </div>

      {isRcmControls ? (
        <div className={listScrollClass}>
          {controlGroups.map((group, gi) => {
            const controls = Array.isArray(group.controls) ? group.controls : [];
            const blockTitle =
              [group.cycleName, group.processName].filter(Boolean).join(' — ') ||
              group.processId ||
              (group.id === 'legacy-flat-controls' ? 'Linked controls' : 'Process');
            return (
              <div
                key={group.id ?? gi}
                className={`${POPOVER_PAD_X} py-4 ${gi > 0 ? 'border-t border-gray-200' : ''}`}
              >
                <p className="text-xs font-semibold leading-snug text-gray-900">{blockTitle}</p>
                {group.processId ? (
                  <p className="mt-1 text-[11px] font-normal text-gray-500">{group.processId}</p>
                ) : null}
                {controls.length > 0 ? (
                  <ul className="mt-3 space-y-2 rounded-md px-3 py-2">
                    {controls.map((c, ci) => (
                      <li key={c.id ?? ci} className="py-0.5 text-xs leading-snug text-gray-900">
                        <span className="font-semibold text-gray-900">{c.controlDisplayId}</span>
                        <span className="font-normal text-gray-400"> — </span>
                        <span className="font-normal">{c.controlTitle}</span>
                        <span className="text-gray-400"> · </span>
                        <button
                          type="button"
                          className="align-baseline text-[11px] font-semibold text-blue-600 hover:text-blue-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            onItemAction({
                              controlId: c.controlId,
                              controlDisplayId: c.controlDisplayId,
                              controlTitle: c.controlTitle,
                            });
                          }}
                        >
                          Control
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-3 text-xs italic text-gray-400">No controls mapped</p>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <ul className={`${listScrollClass} py-0`}>
          {items.map((item, index) => (
            <li
              key={item.id ?? `${item[primaryTextKey]}-${index}`}
              className={`${POPOVER_PAD_X} border-b border-gray-100 py-3.5 last:border-b-0`}
            >
              <p className="text-xs font-semibold text-gray-900">{item[primaryTextKey]}</p>
              {secondaryTextKey && item[secondaryTextKey] && (
                <p className="mt-1 line-clamp-2 text-[11px] text-gray-500">{item[secondaryTextKey]}</p>
              )}
              <button
                type="button"
                className="mt-2 text-[11px] font-semibold text-blue-600 hover:text-blue-700"
                onClick={(e) => {
                  e.stopPropagation();
                  onItemAction(item);
                }}
              >
                {actionLabel}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

AssociationPopover.displayName = 'AssociationPopover';

export default AssociationPopover;
